'use client';
import { Fragment } from 'react';
import { CommentItemWithAction } from '~/app/(default-layout)/s/[storyId]/_components/comment-item-with-action';
import type { UserOverview } from '~/common/types/user-overview';
import { PostCommentContainer } from '@app/(default-layout)/s/[storyId]/_containers/post-comment-container';
import { Loading } from '@app/_components/loading';
import {
  Box,
  BoxProps,
  Divider,
  Paragraph,
  VStack,
  type VstackProps,
} from '@styled-system/jsx';
import { ReplyContainer } from './reply-container';
import { useLocalState } from './use-local-state';

type PostCommentContainerProps = {
  positionType?: 'sticky' | 'fixed';
} & BoxProps;

export type CommentsDetailContainerProps = {
  storyId: string;
  author: UserOverview | undefined;
  postCommentContainerProps?: PostCommentContainerProps;
} & VstackProps;

export const CommentsDetailContainer = ({
  storyId,
  author,
  postCommentContainerProps,
  ...vstackProps
}: CommentsDetailContainerProps) => {
  const {
    tellerUser,
    isLoading,
    handleOnClickLike,
    comments,
    paginationRef,
    replyComment,
    handleOnReply,
    handleOnDelete,
    handleOnDeselectReply,
    handleOnSuccessComment,
    handleOnErrorComment,
  } = useLocalState({
    storyId,
  });

  return (
    <VStack w="100%" {...vstackProps}>
      <VStack w="100%" flex="1">
        {comments.map((comment) => {
          return (
            <Fragment key={comment.id}>
              <VStack w="100%" alignItems="flex-start">
                <CommentItemWithAction
                  storyId={storyId}
                  comment={comment}
                  author={author}
                  isOwner={comment.user.id === tellerUser?.id}
                  onClickLike={handleOnClickLike}
                  onClickReply={handleOnReply}
                  onClickDelete={handleOnDelete}
                  animationName="fade-in"
                  animationDuration="0.2s"
                />

                {comment.replyCount > 0 && (
                  <ReplyContainer
                    replyCount={comment.replyCount}
                    storyId={storyId}
                    commentId={comment.id}
                    author={author}
                  />
                )}
              </VStack>
              <Divider />
            </Fragment>
          );
        })}
      </VStack>

      {comments.length === 0 && (
        <VStack
          w="100%"
          h="100%"
          flex="1"
          justifyContent="flex-end"
          alignItems="center"
          p="4"
        >
          <Paragraph fontWeight={700} color="accent.700" fontSize="size_14">
            👏 最初のコメントを書いて作者に喜んでもらおう！
          </Paragraph>
        </VStack>
      )}

      <Box ref={paginationRef} />

      {isLoading && <Loading />}

      <Box
        w="100%"
        p="4"
        bg="white"
        boxShadow="2xl"
        zIndex="sticky"
        border={{
          base: 'none',
          lg:
            postCommentContainerProps?.positionType === 'fixed'
              ? '0.5px solid token(colors.separator)'
              : 'none',
        }}
        position={postCommentContainerProps?.positionType ?? 'sticky'}
        bottom={{
          base: '0',
          lg: postCommentContainerProps?.positionType === 'fixed' ? '8' : '0',
        }}
        borderRadius={{
          base: '0',
          lg:
            postCommentContainerProps?.positionType === 'fixed' ? '20px' : '0',
        }}
        maxW={{
          base: '100%',
          lg:
            postCommentContainerProps?.positionType === 'fixed'
              ? 'breakpoint-md'
              : 'breakpoint-lg',
        }}
      >
        <PostCommentContainer
          storyId={storyId}
          replyComment={replyComment}
          onDeselectParentComment={handleOnDeselectReply}
          onPostSuccess={handleOnSuccessComment}
          onPostError={handleOnErrorComment}
        />
      </Box>
    </VStack>
  );
};
